import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { decodeJWT } from '@aws-amplify/core';
import type { CognitoSignInSuccessResponse } from '@bvengineering/cas-member-gateway-service-client';
import { ENV } from '@/config/env';
import { withGatewayRefreshPaused } from '@/config/amplifyTokenRefresher';

export async function adoptOtpSession(tokens: CognitoSignInSuccessResponse): Promise<void> {
  const reject = (reason: string) =>
    new Error(`The verification session cannot be used to sign in (${reason}).`);
  let access;
  let id;
  try {
    access = decodeJWT(tokens.accessToken);
    id = decodeJWT(tokens.idToken);
  } catch {
    throw reject('tokens are not valid JWTs');
  }

  const issuer = `https://cognito-idp.${ENV.cognito.region}.amazonaws.com/${ENV.cognito.userPoolId}`;
  const now = Date.now() / 1000;
  const isFresh = (exp: unknown) => typeof exp === 'number' && exp > now;
  const accessClaims = access.payload;
  const idClaims = id.payload;
  const username = typeof accessClaims.username === 'string' ? accessClaims.username : '';
  const issuedAt = typeof accessClaims.iat === 'number' ? accessClaims.iat : 0;

  if (accessClaims.iss !== issuer || idClaims.iss !== issuer) {
    throw reject("token issuer is not this app's user pool");
  }
  if (accessClaims.token_use !== 'access' || idClaims.token_use !== 'id') {
    throw reject('unexpected token type');
  }
  if (!accessClaims.sub || accessClaims.sub !== idClaims.sub) {
    throw reject('access and ID tokens belong to different users');
  }
  if (!username || !issuedAt) throw reject('access token is missing required claims');
  if (!isFresh(accessClaims.exp) || !isFresh(idClaims.exp)) throw reject('a token is expired');
  if (!tokens.refreshToken) throw reject('refresh token is missing');

  const orchestrator = cognitoUserPoolsTokenProvider.tokenOrchestrator;
  try {
    // setTokens writes several storage keys in turn, so a failure can leave a partial session.
    await withGatewayRefreshPaused(() =>
      orchestrator.setTokens({
        tokens: {
          accessToken: access,
          idToken: id,
          refreshToken: tokens.refreshToken,
          username,
          clockDrift: issuedAt * 1000 - Date.now(),
        },
      })
    );
    await getCurrentUser();
    const session = await fetchAuthSession();
    if (!session.tokens) throw new Error('no tokens');
  } catch {
    await orchestrator.clearTokens();
    throw reject('Amplify could not store or load the session');
  }
}
